import { API_BASE_URL, ENDPOINTS } from '@/config/api.enpoints';
import type { CompanyContact, GetMeetingsParams, Paginated } from '@/types/meeting';

import apiClient from './apiClient';
import type { CustomerData, MeetingTypeCount } from './types';

type Scoring = {
  [category: string]: {
    [metric: string]: number;
  };
};

export interface Meeting {
  updatedAt: string;
  overallScore?: object;
  avargerscroing?: number;
  mongoRefId?: string;
  customerId?: string;
  meetingtypecount?: MeetingTypeCount;
  customerData?: CustomerData;
  scriptType: string;
  company: string;
  category: string;
  scoreDetails: {
    overallScore: string;
  };
  user_id: string;
  customer_id: string;
  tenantId: string;
  meetingType: string;
  uploaded_audio_url: string;
  transcript_text: string;
  ai_summary: string;
  ai_score: Scoring;
  status: string;
  createdBy: string;
  updatedBy: string;
  id: string;
  created_at: string;
  companyContact?: CompanyContact;
  score?: number | string | undefined;
}

export interface MeetingSummaryDTO {
  summary: string;
}

export type MeetingByIdResult = {
  status: boolean;
  message: string;
  data: Meeting | MeetingSummaryDTO;
};

export const getMeetings = async (params: GetMeetingsParams): Promise<Paginated<Meeting>> => {
  const { data } = await apiClient.get<Paginated<Meeting>>(
    `${API_BASE_URL}${ENDPOINTS.Meeting.base}`,
    { params },
  );
  return data;
};

export const getMeetingById = async (
  meetingId: string | undefined,
  summaryOnly = false,
): Promise<Meeting | MeetingSummaryDTO> => {
  const res = await apiClient.get<MeetingByIdResult>(
    `${API_BASE_URL}${ENDPOINTS.Meeting.base}/${meetingId}`,
    { headers: { 'X-Get-Summary': summaryOnly } },
  );
  return res.data.data;
};

export const getAdminMeetingById = async (
  meetingId: string | undefined,
  summaryOnly = false,
): Promise<Meeting | MeetingSummaryDTO> => {
  const res = await apiClient.get<MeetingByIdResult>(
    `${API_BASE_URL}${ENDPOINTS.Meeting.base}/admin/${meetingId}`,
    { headers: { 'X-Get-Summary': summaryOnly } },
  );
  return res.data.data;
};

// Admin endpoint for card view with pagination
export interface AdminMeetingCardParams {
  view?: string;
  page?: number;
  pageSize?: number;
  orderBy?: string;
}

export const getAdminMeetingsCard = async (
  params: AdminMeetingCardParams,
): Promise<Paginated<Meeting>> => {
  const { data } = await apiClient.get<Paginated<Meeting>>(
    `${API_BASE_URL}${ENDPOINTS.Meeting.base}/admin`,
    { params: { view: 'card', page: 1, pageSize: 10, orderBy: 'desc', ...params } },
  );
  return data;
};

export interface CategoryComparisonItem {
  category: string;
  practiceAvg: number;
  realScore: number;
  delta: number;
  status: 'IMPROVED' | 'GAP_DETECTED' | 'CONSISTENT';
  insight: string;
}

export interface PracticeHistorySummaryItem {
  id: string;
  title: string;
  type: string;
  score: number;
  date: string;
}

export interface PracticeComparisonResponse {
  hasPracticeHistory: boolean;
  companyName: string;
  contactName?: string | null;
  attendees?: string[];
  meetingTitle?: string;
  scheduledWith?: string | null;
  practiceCount: number;
  practiceBaselineScore: number;
  realMeetingScore: number;
  overallImprovementPercentage: number;
  performanceDelta: 'POSITIVE_GROWTH' | 'STEADY' | 'NEEDS_ATTENTION';
  categoryComparison: CategoryComparisonItem[];
  whatImprovedInRealCall: string[];
  criticalExecutionGaps: string[];
  unhandledRealObjections: string[];
  actionableRoadmapToCloseGaps: string[];
  practiceHistoryList: PracticeHistorySummaryItem[];
  message?: string;
}

export const getMeetingPracticeComparison = async (
  meetingId: string,
): Promise<PracticeComparisonResponse> => {
  const res = await apiClient.get<{
    status: boolean;
    message: string;
    data: PracticeComparisonResponse;
  }>(`${API_BASE_URL}${ENDPOINTS.Meeting.base}/practice-comparison/${meetingId}`);
  return res.data.data;
};